import { CircleTickIcon } from '@helloself/hello-self-ui';
import classNames from 'classnames';
import { useField } from 'formik';
import { ChangeEvent, FocusEvent, useMemo, useState } from 'react';

import { Error } from './Error';
import { Info } from './Info';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label: string;
    name: string;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    placeholder?: string;
    className?: string;
    hideValidationIcon?: boolean;
    info?: string;
    search?: boolean;
}

export const Input = ({
    label,
    name,
    id,
    type,
    value,
    onChange,
    placeholder,
    disabled,
    className,
    hideValidationIcon = false,
    info,
    search = false,
}: InputProps) => {
    const [isFocused, setIsFocused] = useState(false);
    const [field, meta] = useField(name);

    const showError = useMemo(() => !!meta.error && meta.touched, [meta.error, meta.touched]);
    const valid = useMemo(() => !meta.error && field.value, [meta.error, field.value]);

    const handleFocus = () => {
        setIsFocused(true);
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
        if (!e.target.value) {
            setIsFocused(false);
        }
    };

    return (
        <div className="space-y-2">
            <div className="relative">
                <input
                    type={type}
                    id={id ?? name}
                    name={name}
                    className={
                        `${className} ${search && 'input-search pl-10'} peer w-full bg-white border ${
                            showError
                                ? 'border-danger-300'
                                : valid
                                ? 'border-green-300 pr-10'
                                : isFocused
                                ? 'border-blue-400'
                                : 'border-neutral-400'
                        } rounded-lg px-3 pt-6 pb-3 focus:outline-none text-neutral-400 disabled:opacity-50 autofill:placeholder-transparent ` +
                        classNames({
                            'placeholder-transparent': !isFocused,
                        })
                    }
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChange={onChange}
                    value={value}
                    placeholder={placeholder}
                    disabled={disabled}
                />
                <label
                    htmlFor={name}
                    className={`absolute ${
                        search ? 'left-10' : 'left-3'
                    } top-1/2 transform transition-all duration-150 ease-in-out pointer-events-none 
                        ${
                            isFocused || value || type === 'date'
                                ? 'text-xs -translate-y-5'
                                : 'text-base -translate-y-1/2'
                        } text-neutral-400 peer-autofill:text-xs peer-autofill:-translate-y-5`}
                >
                    {label}
                </label>
                {valid && !hideValidationIcon && (
                    <CircleTickIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                )}
            </div>

            {showError && <Error>{meta.error}</Error>}
            {info && <Info info={info} />}
        </div>
    );
};
